import type { UserRole } from './widget-registry';

/**
 * A BE `Role` enum NEVESÍTETT tükre (SSoT: apps/kgc-api/prisma/schema/enums.prisma).
 *
 * Szándékosan explicit lista, nem generált típus: a `backend-role-map.spec.ts`
 * drift-őre a séma-fájlból olvassa vissza az értékkészletet, és BUKIK, ha a kettő
 * eltér — így egy új BE-szerep a CI-t pirosítja, nem a felhasználót hagyja
 * üres dashboarddal (QA-100).
 */
export const BACKEND_ROLES = [
  'OPERATOR',
  'TECHNIKUS',
  'BOLTVEZETO',
  'ACCOUNTANT',
  'PARTNER_OWNER',
  'CENTRAL_ADMIN',
  'DEVOPS_ADMIN',
  'SUPER_ADMIN',
] as const;

export type BackendRole = (typeof BACKEND_ROLES)[number];

/**
 * BE `Role` → FE dashboard-szerep, EXPLICIT hozzárendeléssel mind a 8 értékre.
 *
 * `null` = a szerep **jelölt, de termék-döntésre vár** (QA-100 ③: TECHNIKUS ·
 * ACCOUNTANT · PARTNER_OWNER). A null-ág a MAI viselkedést tartja: csak a
 * szerep-független widgetek jelennek meg.
 *
 * ⚠️ Pontosan: ez a térkép UI-LÁTHATÓSÁGOT szabályoz, nem hozzáférést. A BE
 * dashboard-végpontjain `@Roles` 0/13, és 12/13 controller `JwtAuthGuard`-ot
 * használ önmagában (a 13. mögött `AdminOnlyGuard` áll); a 12 védtelen controller
 * kizárólag `@Get` (⚙️ mérése és önkorrekciója, 2026-08-24). Az olvasás tehát
 * szerep-szűretlen: a térkép szigorítása nem zárja el az adatot, a lazítása nem
 * nyit új utat. A végponti szerep-védelem külön tétel.
 */
export const BACKEND_ROLE_TO_DASHBOARD_ROLE: Record<BackendRole, UserRole | null> = {
  OPERATOR: 'OPERATOR',
  BOLTVEZETO: 'STORE_MANAGER',
  CENTRAL_ADMIN: 'ADMIN',
  DEVOPS_ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
  // ── termék-döntésre vár (QA-100 ③) ──
  TECHNIKUS: null,
  ACCOUNTANT: null,
  PARTNER_OWNER: null,
};

/** A `null`-lal jelölt, döntésre váró szerepek — a drift-őr és a doksi közös forrása. */
export const PENDING_DECISION_ROLES = BACKEND_ROLES.filter(
  role => BACKEND_ROLE_TO_DASHBOARD_ROLE[role] === null
);

export function isBackendRole(value: unknown): value is BackendRole {
  return typeof value === 'string' && (BACKEND_ROLES as readonly string[]).includes(value);
}

/**
 * A bejelentkezett felhasználó BE-szerepét dashboard-szereppé oldja fel.
 *
 * A korábbi `(user?.role as UserRole)` cast helyett áll: a cast némán átengedte
 * a `BOLTVEZETO`-t, amire se layout, se widget-szabály nem illeszkedett — a
 * boltvezető 37-ből 3 widgetet kapott, scanner-layouton (QA-100).
 *
 * - hiányzó / üres szerep → `null` (FAIL-CLOSED, az M-033 döntés szándéka szerint:
 *   üres szerep NEM kap implicit OPERATOR-jogot). ⚠️ Ez a `pages/`-ág korábbi
 *   `|| 'OPERATOR'` alapértelmezéséhez képest SZŰKÍT — a két belépő eddig
 *   ellentétesen viselkedett ugyanarra a bemenetre.
 * - ismert, de döntésre váró szerep → `null` (mai, minimális nézet)
 * - ismeretlen érték → `null`; a fejlesztési idejű esetet a drift-őr fogja meg
 */
export function resolveDashboardRole(rawRole: string | null | undefined): UserRole | null {
  if (!rawRole) return null;
  if (!isBackendRole(rawRole)) return null;

  return BACKEND_ROLE_TO_DASHBOARD_ROLE[rawRole];
}
